import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Offset pagination control for the admin lists.
 *
 * Paging is server-side: the backend returns `{items, total, limit, offset}`,
 * so these controls move a window over the whole result set rather than over
 * whatever happened to be loaded in the browser.
 */
export function Pagination({ total, limit, offset, onChange, isLoading }) {
  const safeLimit = limit || 25;
  const safeTotal = total || 0;
  const page = Math.floor(offset / safeLimit) + 1;
  const pageCount = Math.max(1, Math.ceil(safeTotal / safeLimit));
  const from = safeTotal === 0 ? 0 : offset + 1;
  const to = Math.min(offset + safeLimit, safeTotal);

  const canGoBack = offset > 0 && !isLoading;
  const canGoForward = offset + safeLimit < safeTotal && !isLoading;

  // The window can end up past the end of a list that shrank underneath it -
  // another operator resolved the cases, or a background job completed the
  // transfers. Without this the page is blank with no explanation of why.
  const isStranded = safeTotal > 0 && offset >= safeTotal;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
      <p className="text-xs text-slate-400">
        {isStranded ? (
          <>
            This page is past the end of {safeTotal} result
            {safeTotal === 1 ? "" : "s"}.{" "}
            <button
              type="button"
              onClick={() => onChange(0)}
              className="font-semibold text-[#0A4730] underline underline-offset-4"
            >
              Back to the first page
            </button>
          </>
        ) : safeTotal === 0 ? (
          "No results"
        ) : (
          `Showing ${from}–${to} of ${safeTotal}`
        )}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, offset - safeLimit))}
          disabled={!canGoBack}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-[#0A4730] disabled:opacity-40 hover:bg-slate-50 transition"
        >
          <ChevronLeft size={14} /> Previous
        </button>
        <span className="text-xs text-slate-500 tabular-nums px-2">
          Page {Math.min(page, pageCount)} of {pageCount}
        </span>
        <button
          type="button"
          onClick={() => onChange(offset + safeLimit)}
          disabled={!canGoForward}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-[#0A4730] disabled:opacity-40 hover:bg-slate-50 transition"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
